import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import modalController from 'src/decorators/ModalController'
import {
  formatDate as formatDateTool,
  formatFullDate as formatFullDateTool,
  formatShortDate as formatShortDateTool
} from '@jarvis.oddle/core/tools/date'
import { languages } from '@jarvis.oddle/core/tools/localisation'
import { consumeQueryParam } from '@jarvis.oddle/core/tools/routes'
import { formatMoney as formatMoneyTool, isEmpty } from 'src/utils/string'
import {
  DEFAULT_PRODUCT_IMAGE_URL,
  IMAGEKIT_ID,
  ODDLE_PASS_CLIENT_ID
} from 'config/client'
import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { redirect } from 'src/utils/routes'
import useTracker from 'src/hooks/useTracker'
import { parseJwt } from 'tools/jwtParser'
import { useRouter } from 'next/router'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectAppContext } from 'src/redux/selector'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import {
  fetchStoreOrderAction,
  prepareCheckoutAction
} from 'src/redux/order/action'
import {
  fetchUserAction,
  getAllAddressesOPAction,
  oddlePassV5LoginAction
} from 'src/redux/user'
import { getStoreOrderMetaDataAction } from 'src/redux/checkout'

// check commented context as may not be no longer consumed
interface AppContextState {
  formatMoney: (price: number) => string
  // getImagePath: (x) => x // no longer used
  getLogoImagePath: () => string
  getProductImagePath: (
    url: string,
    returnDefault?: boolean,
    options?: Partial<{
      shouldCache: boolean
      containerWidth: number
      containerHeight: number
      objectFit: string
      useUcareCdnScaleCrop?: boolean
    }>
  ) => string
  redirectToCheckout: (storeOrderId: string) => void
  formatShortDate: (date: string) => string
  formatFullDate: (date: string | Date) => string
  formatDate: (date: string, options?: Record<string, unknown>) => string
  // formatAddressString: (x) => x // no longer used
  // isAccessedByPublicUrl: (x) => x // use redux
  // formatDisplacement: (x) => x // use utils
  // themeSettingData: {} // use redux
  prepareToCheckout: (
    options: Partial<{
      onReadyToCheckout: (storeOrder: Oms.V4.StoreOrder) => void
      onNotReadyToCheckout: (error: Error, errorCode: string) => void
      onShouldRefresh: (callback?: VoidFunction) => void
    }>
  ) => void
  setFireSignedInEventToSegmentIoWithMca: (bool?: boolean) => void
  tokenFromEats: string
  setTokenFromEatsToNull: VoidFunction
}

const AppContext = createContext({} as AppContextState)

interface AppContextProviderProps {
  children: ReactNode
}

const AppContextProvider = ({
  children
}: AppContextProviderProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const { isBot, meta, storeOrder, user } = useAppSelector(selectAppContext)
  const dispatch = useAppDispatch()

  const router = useRouter()
  const { locale, referrer } = router.query as Record<
    string,
    string | undefined
  >

  const {
    enableImageKit,
    currencySymbol,
    currencyCode,
    decimalFormat,
    imagePath,
    logoImage
  } = meta?.data || {}
  const { segmentTrackerCheckoutStarted } = useTracker()

  const [currentLocale, setCurrentLocale] = useState(locale)
  const [tokenFromEats, setTokenFromEats] = useState(null)
  const [
    fireSignedInEventToSegmentIoWithMca,
    setFireSignedInEventToSegmentIoWithMca
  ] = useState(false)

  const formatMoney = useCallback(
    (price: number) =>
      formatMoneyTool(price, currencySymbol || currencyCode, decimalFormat),
    [currencyCode, currencySymbol, decimalFormat]
  )

  const getLogoImagePath = useCallback(
    () => `${imagePath}logo/${logoImage}.jpg`,
    [imagePath, logoImage]
  )

  const getProductImagePath: AppContextState['getProductImagePath'] =
    useCallback(
      (url, returnDefault = true, options = {}) => {
        const {
          shouldCache,
          containerWidth,
          containerHeight,
          objectFit,
          useUcareCdnScaleCrop
        } = options
        if (isEmpty(url) || url === DEFAULT_PRODUCT_IMAGE_URL) {
          return returnDefault ? DEFAULT_PRODUCT_IMAGE_URL : null
        }

        if (url.includes('ucarecdn')) {
          // imageKitWidth and imageKitHeight control how the image needs to be resized
          const imageUcareWidth = !isNaN(containerWidth)
            ? `${containerWidth}x`
            : ''
          const imageUcareHeight = !isNaN(containerHeight)
            ? `${isNaN(containerWidth) ? 'x' : ''}${containerHeight}`
            : ''
          let imageUrl = url
          if (imageUrl.endsWith('/')) {
            // remove slash from end of url
            imageUrl = imageUrl.slice(0, -1)
          }
          return `${imageUrl}${
            imageUcareWidth || imageUcareHeight
              ? `/-/${
                  useUcareCdnScaleCrop ? 'scale_crop' : 'resize'
                }/${imageUcareWidth}${imageUcareHeight}/${
                  useUcareCdnScaleCrop ? 'smart/' : ''
                }`
              : ''
          }`
        }

        if (!shouldCache) {
          return returnDefault ? DEFAULT_PRODUCT_IMAGE_URL : null
        }

        if (enableImageKit) {
          // imageKitWidth and imageKitHeight control how the image needs to be resized
          const imageKitWidth = !isNaN(containerWidth)
            ? `w-${containerWidth}`
            : ''
          const imageKitHeight = !isNaN(containerHeight)
            ? `h-${containerHeight}`
            : ''
          if (url.includes('http')) {
            return `https://ik.imagekit.io/${IMAGEKIT_ID}/${url}?tr=${imageKitWidth},${imageKitHeight}`
          }
          return `https://ik.imagekit.io/${IMAGEKIT_ID}/${imagePath}product/${url}.jpg?tr=${imageKitWidth},${imageKitHeight}`
        }

        // weserveWidth and weserveHeight control how the image needs to be resized
        const weserveWidth = !isNaN(containerWidth)
          ? `&w=${containerWidth}`
          : ''
        const weserveHeight = !isNaN(containerHeight)
          ? `&h=${containerHeight}`
          : ''
        const weserveFit = !isEmpty(objectFit)
          ? `&fit=${objectFit}`
          : '&fit=cover'
        if (url.includes('http')) {
          return `//images.weserv.nl/?url=${url}${weserveWidth}${weserveHeight}${weserveFit}`
        }
        return `//images.weserv.nl/?url=${imagePath}product/${url}.jpg${weserveWidth}${weserveHeight}${weserveFit}`
      },
      [enableImageKit, imagePath]
    )

  const prepareToCheckout: AppContextState['prepareToCheckout'] = useCallback(
    async ({
      onReadyToCheckout,
      onNotReadyToCheckout,
      onShouldRefresh
    } = {}) => {
      const checkoutResponse = await dispatch(prepareCheckoutAction())
      if (
        checkoutResponse.meta.requestStatus === 'fulfilled' &&
        checkoutResponse.payload.payload.ok &&
        checkoutResponse.payload.payload.storeOrder
      ) {
        const storeOrder = checkoutResponse.payload.payload.storeOrder
        typeof onReadyToCheckout === 'function' && onReadyToCheckout(storeOrder)
      } else {
        const errors = Array.isArray(checkoutResponse.payload.payload.errors)
          ? checkoutResponse.payload.payload.errors
          : [checkoutResponse.payload.payload.errors]
        const errorCodes = Array.isArray(
          checkoutResponse.payload.payload.errorCodes
        )
          ? checkoutResponse.payload.payload.errorCodes
          : [checkoutResponse.payload.payload.errorCodes]
        if (errors.includes('store_order_not_found')) {
          modalController.showModal('RefreshModal')
          typeof onShouldRefresh === 'function' && onShouldRefresh()
        } else {
          typeof onNotReadyToCheckout === 'function' &&
            onNotReadyToCheckout(errors, errorCodes)
        }
      }
    },
    [dispatch]
  )

  const fireSignedInEventToSegmentIoAction = useCallback(
    (
      payload: Partial<{
        ocaId: string
        firstName: string
        lastName: string
        email: string
        phone: string
      }> = {}
    ) => {
      // fire signed in event to segment.io
      const parsedToken = parseJwt(window.localStorage.getItem('accessToken'))
      const continueWithOddlePass =
        parsedToken.state.continueWithOddlePass || false
      const { ocaId, firstName, lastName, email, phone } = payload
      dispatch(
        oddlePassV5LoginAction({
          id: ocaId,
          firstName,
          lastName,
          email,
          phone,
          continueWithOddlePass
        })
      )
    },
    [dispatch]
  )

  const redirectToCheckout = useCallback(
    async (storeOrderId) => {
      const {
        data: { id, menuId }
      } = storeOrder
      const {
        oddlePassEnabled,
        newOddlePassFlow,
        enableOcaAddress,
        customerSegmentEnabled
      } = meta.data || {}
      const validQuery = Object.keys(router.query)
        .filter((query) => !['locale', 'slug', 'product'].includes(query))
        .reduce(
          (obj, query) => ({
            ...obj,
            [query]: router.query[query]
          }),
          {}
        )
      const preservedQuery = `?${new URLSearchParams(validQuery).toString()}`
      const enableNewCheckoutFlow = meta.data?.features?.enableNewCheckoutFlow
      const query =
        preservedQuery.length > 1
          ? `${preservedQuery}${
              enableNewCheckoutFlow ? '&step_address=true' : ''
            }`
          : `${enableNewCheckoutFlow ? '?step_address=true' : ''}`
      // should fetch user to checking session
      const { clientId } =
        typeof window !== 'undefined' && window.localStorage
          ? parseJwt(window.localStorage.getItem('accessToken') || '{}')
          : { clientId: null }
      const {
        meta: metaFetchUser,
        payload: { payload }
      } = await dispatch(
        fetchUserAction(
          !isEmpty(clientId) && clientId !== ODDLE_PASS_CLIENT_ID
            ? { clientId }
            : {}
        )
      )

      // when user login from eats to shop and oddle paas will verify user login when user proceed to checkout
      if (payload.error === 'customer_require_verification') {
        await window.opw.verificationAccount(menuId, {
          path: `/[locale]/checkout/[storeOrderId]`,
          as: `/${locale}/checkout/${id || storeOrderId}${query}`,
          redirectToCheckoutPage: true
        })
      }

      if (metaFetchUser.requestStatus === 'fulfilled' && payload.id) {
        if (fireSignedInEventToSegmentIoWithMca) {
          fireSignedInEventToSegmentIoAction(payload)
          setFireSignedInEventToSegmentIoWithMca(false)
        }
        if (oddlePassEnabled && newOddlePassFlow && enableOcaAddress) {
          await dispatch(getAllAddressesOPAction())
        }
        if (customerSegmentEnabled && typeof window !== 'undefined') {
          const { payload: storeOrderMetaData } = await dispatch(
            getStoreOrderMetaDataAction({
              storeOrderId: id || storeOrderId
            })
          )
          segmentTrackerCheckoutStarted(storeOrderMetaData)
        }
        if (typeof window !== 'undefined') {
          // use hard routing, due to transition from old landing page to the new checkout page doesn't trigger server rendering side
          redirect({
            url: `/[locale]/checkout/${id || storeOrderId}${query}`,
            as: `/${locale}/checkout/${id || storeOrderId}${query}`,
            shallow: true
          })
        }
      }
    },
    [
      dispatch,
      fireSignedInEventToSegmentIoAction,
      fireSignedInEventToSegmentIoWithMca,
      locale,
      meta.data,
      router.query,
      segmentTrackerCheckoutStarted,
      storeOrder
    ]
  )

  const formatShortDate: AppContextState['formatShortDate'] = useCallback(
    (date) => formatShortDateTool({ t, date, locale }),
    [locale, t]
  )

  const formatFullDate: AppContextState['formatFullDate'] = useCallback(
    (date) => formatFullDateTool({ t, date, locale }),
    [locale, t]
  )

  const formatDate: AppContextState['formatDate'] = useCallback(
    (date, options = {}) => formatDateTool({ t, date, locale, ...options }),
    [locale, t]
  )

  useEffect(() => {
    const accessToken = consumeQueryParam('accessToken')
    try {
      if (
        typeof window !== 'undefined' &&
        !window.localStorage.getItem('accessToken') &&
        accessToken &&
        referrer
      ) {
        setTokenFromEats(accessToken)
      }
    } catch (e) {
      console.error(e)
    }

    if (isBot) {
      return
    }

    const asyncAction = async () => {
      // The fetchStoreOrderAction shouldn't be called from the check-out page and the payment invoice page,
      // because it always makes the /api/stripe/charges api responses with error payment_fail
      if (
        ['fulfilled', 'rejected'].includes(user.status) &&
        storeOrder.status !== 'fulfilled' &&
        router.pathname !== '/[locale]/checkout/[storeOrderId]' &&
        router.pathname !== '/[locale]/invoice/[storeOrderId]'
      ) {
        // this data to get isPickup and deliveryAddress value, this value will be used to makesure user has selected dining preferences

        try {
          const { meta, payload } = await dispatch(fetchStoreOrderAction())
          if (meta.requestStatus === 'fulfilled' && payload?.storeOrder) {
            const {
              storeOrder: { isPickup, deliveryAddress }
            } = payload
            const isVisited = isPickup || (!isPickup && !!deliveryAddress)
            // check the result to decide whether user has selected dining preference or not
            if (isVisited) {
              pickUpDeliveryFlow.updateMeta({ isVisited })
            }
          }
        } catch (error) {
          console.log(error)
        }
      }
    }

    asyncAction()
    // eslint-disable-next-line
  }, [user.status])

  useEffect(() => {
    if (currentLocale !== locale) {
      showNotificationToast.success(
        t('Language changed to _language_.', {
          language: languages[locale]
        })
      )
      setCurrentLocale(locale)
    }
  }, [currentLocale, locale, t])

  const setTokenFromEatsToNull = () => setTokenFromEats(null)

  const setFireSignedInEventToSegmentIoWithMcaWithDefault = (bool = false) =>
    setFireSignedInEventToSegmentIoWithMca(bool)

  const contextState = useMemo(
    () => ({
      formatMoney,
      getLogoImagePath,
      getProductImagePath,
      prepareToCheckout,
      redirectToCheckout,
      formatShortDate,
      formatFullDate,
      formatDate,
      setTokenFromEatsToNull,
      setFireSignedInEventToSegmentIoWithMca:
        setFireSignedInEventToSegmentIoWithMcaWithDefault,
      tokenFromEats
    }),
    [
      formatDate,
      formatFullDate,
      formatMoney,
      formatShortDate,
      getLogoImagePath,
      getProductImagePath,
      prepareToCheckout,
      redirectToCheckout,
      tokenFromEats
    ]
  )

  return (
    <AppContext.Provider value={contextState}>{children}</AppContext.Provider>
  )
}

export const useAppContext = (): AppContextState => useContext(AppContext)

export default AppContextProvider
